import { useEffect, useRef, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Heading,
  Grid,
  GridItem,
  Button,
  Icon,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Gift,
  Users,
  Trophy,
  Search,
  Truck,
  Scale,
  ShoppingCart,
} from "lucide-react";

const contestEndDate = new Date("2026-05-01T00:00:00");

const getTimeLeft = () => {
  const diff = Math.max(0, contestEndDate.getTime() - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const Countdown = () => {
  const [time, setTime] = useState(getTimeLeft);
  useEffect(() => {
    const id = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <Flex justify="center" gap={{ base: 4, md: 6 }}>
      {Object.entries(time).map(([label, val]) => (
        <Flex
          key={label}
          direction="column"
          align="center"
          bg="whiteAlpha.200"
          borderRadius="lg"
          px={{ base: 4, md: 6 }}
          py={3}
          minW={{ base: "60px", md: "70px" }}
        >
          <Text
            fontSize={{ base: "3xl", md: "5xl" }}
            fontWeight="extrabold"
            color="blue.400"
            lineHeight={1}
          >
            {val}
          </Text>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            fontWeight="medium"
            color="gray.400"
            mt={1}
          >
            {label}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const detailCards = [
  {
    icon: Gift,
    title: "$1,000 in Gift Cards",
    description:
      "$500 Palmetto State Armory gift cards for the two grand prize winners!",
  },
  {
    icon: Users,
    title: "10 Shirt Winners",
    description: "Exclusive BulletScout shirts for 10 lucky runners-up.",
  },
  {
    icon: Trophy,
    title: "Free to Enter",
    description: "No purchase necessary. Just sign up and you're in.",
  },
];

const howItWorks = [
  {
    icon: Search,
    title: "Search Ammo",
    description:
      "Search by caliber, brand, or product — just like a normal ammo site, but without being locked into one retailer.",
  },
  {
    icon: Truck,
    title: "We Show You the Delivered Cost (Price + Shipping) to Your ZIP",
    description:
      "We calculate each retailer's actual shipping cost to your ZIP and include it in the price — so the CPR you see is the delivered cost, not an estimate.",
  },
  {
    icon: Scale,
    title: "You Compare the True Cost Per Round",
    description:
      "Prices are shown as delivered cost per round, so you can compare retailers apples-to-apples without clicking through multiple checkout pages.",
  },
  {
    icon: ShoppingCart,
    title: "Buy Direct From the Retailer",
    description:
      "When you find the deal you want, we send you straight to the retailer's site to complete your purchase. We never sell ammo or take payment.",
  },
];

function GleamWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const link = document.createElement("a");
    link.className = "e-widget no-button";
    link.href =
      "https://gleam.io/N3284/find-the-best-ammo-deals-win-500-to-spend-at-psa";
    link.rel = "nofollow";
    link.textContent = "Find the Best Ammo Deals… & Win $500 to Spend at PSA!";
    container.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://widget.gleamjs.io/e.js";
    script.async = true;
    container.appendChild(script);

    return () => {
      container.innerHTML = "";
    };
  }, []);

  return <Box ref={containerRef} w="full" />;
}

export default function Giveaway() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box>
      <Helmet>
        <title>PSA Gift Card Giveaway | BulletScout</title>
        <meta
          name="description"
          content="Enter to win a $500 Palmetto State Armory gift card. Two grand prize winners plus 10 BulletScout shirt winners. Free to enter!"
        />
      </Helmet>

      {/* Hero */}
      <Box py={{ base: 10, md: 16 }} px={4} textAlign="center">
        <Box maxW="4xl" mx="auto">
          <Heading
            as="h1"
            fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
            fontWeight="bold"
            mb={4}
          >
            Palmetto State Armory Giftcard Giveaway by{" "}
            <Text as="span" color="blue.400">
              BulletScout.com
            </Text>
            !
          </Heading>
        </Box>
      </Box>

      {/* 3 detail cards */}
      <Box px={4} pb={{ base: 10, md: 16 }}>
        <Grid
          maxW="4xl"
          mx="auto"
          templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
          gap={{ base: 4, md: 6 }}
        >
          {detailCards.map((card) => (
            <GridItem
              key={card.title}
              borderWidth="1px"
              borderRadius="lg"
              p={{ base: 5, md: 6 }}
              textAlign="center"
            >
              <Flex justify="center" mb={3}>
                <Icon
                  as={card.icon}
                  boxSize={{ base: 7, md: 8 }}
                  color="blue.400"
                />
              </Flex>
              <Text
                fontWeight="bold"
                fontSize={{ base: "md", md: "lg" }}
                mb={1}
              >
                {card.title}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                {card.description}
              </Text>
            </GridItem>
          ))}
        </Grid>
      </Box>

      {/* Gleam + Countdown + Image */}
      <Box px={4} py={{ base: 8, md: 12 }}>
        <Box maxW="4xl" mx="auto">
          {/* Gleam Widget */}
          <Box mb={{ base: 8, md: 10 }}>
            <GleamWidget />
          </Box>

          {/* Countdown */}
          <Box textAlign="center" mb={{ base: 8, md: 10 }}>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="widest"
              color="gray.500"
              mb={3}
            >
              Ends In:
            </Text>
            <Countdown />
          </Box>

          {/* How BulletScout Works */}
          <Box mb={{ base: 10, md: 12 }}>
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              textAlign="center"
              mb={{ base: 8, md: 10 }}
            >
              How BulletScout Works
            </Heading>
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              }}
              gap={{ base: 4, md: 6 }}
            >
              {howItWorks.map((item) => (
                <GridItem
                  key={item.title}
                  borderWidth="1px"
                  borderRadius="lg"
                  p={{ base: 5, md: 6 }}
                >
                  <Flex
                    w={10}
                    h={10}
                    borderRadius="full"
                    bg="blue.500"
                    align="center"
                    justify="center"
                    mb={4}
                  >
                    <Icon as={item.icon} boxSize={5} color="white" />
                  </Flex>
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "sm", md: "md" }}
                    mb={2}
                  >
                    {item.title}
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                    {item.description}
                  </Text>
                </GridItem>
              ))}
            </Grid>

            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              fontWeight="bold"
              textAlign="center"
              mt={{ base: 8, md: 10 }}
              maxW="3xl"
              mx="auto"
              px={2}
            >
              Buying ammo online shouldn't require opening ten tabs, guessing
              shipping costs, or getting burned at checkout. BulletScout is a
              free ammo price comparison search engine built for shooters who
              want to see the real, delivered cost per round before they buy.
            </Text>

            <Flex justify="center" mt={{ base: 6, md: 8 }}>
              <Button
                as={Link}
                to="/"
                colorScheme="blue"
                size="lg"
                fontWeight="extrabold"
                fontSize={{ base: "md", md: "lg" }}
                px={{ base: 8, md: 10 }}
                py={{ base: 5, md: 6 }}
                h="auto"
                w={{ base: "full", sm: "auto" }}
              >
                Search For Ammo Now
              </Button>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
